import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Button, Input, InputNumber, DatePicker, Space, Typography, Tag,
  message, Modal, List, Avatar, Select, Empty, Skeleton, Divider,
} from 'antd';
import {
  RocketOutlined, PlusOutlined, EnvironmentOutlined, DollarOutlined,
  CalendarOutlined, MessageOutlined, ArrowLeftOutlined, SendOutlined,
  EditOutlined, DeleteOutlined, StopOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';
import { absoluteUploadUrl } from '../../utils/uploadUrl';

const { Title, Text } = Typography;
const { TextArea } = Input;
const BRAND = '#25D366';
const BRAND_DARK = '#128C7E';

function fmtDate(iso) {
  if (!iso) return 'A combinar';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Modalidades de futebol comuns no Brasil. Ordem = frequencia esperada.
const GAME_TYPES = [
  { value: 'society', label: 'Society (7x7 na grama)' },
  { value: 'fut7',    label: 'Fut 7' },
  { value: 'futsal',  label: 'Futsal (salão)' },
  { value: 'campo',   label: 'Campo (11x11)' },
  { value: 'showbol', label: 'Showbol' },
  { value: 'futevolei', label: 'Futevôlei' },
  { value: 'outro',   label: 'Outro' },
];
const gameTypeLabel = (v) => GAME_TYPES.find(g => g.value === v)?.label || v;

// Tela unificada de amistosos: lista publica + detalhe interno + chat.
// Rota: /amistosos
export default function FriendlyMatches() {
  const [view, setView] = useState('list'); // list | detail | create
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatchId, setSelectedMatchId] = useState(null);
  const [myTeams, setMyTeams] = useState([]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/community/friendlies');
      setMatches(r.data?.matches || []);
    } catch { message.error('Erro ao carregar'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  useEffect(() => {
    api.get('/community/team-owner/my').then(r => setMyTeams(r.data?.teams || r.data || [])).catch(() => {});
  }, []);

  // Deep-link: /time/amistosos?match_id=X abre direto no detalhe. Usado
  // pela tela 'Meus amistosos' pra continuar navegacao no card.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mid = parseInt(params.get('match_id'), 10);
    if (Number.isFinite(mid)) {
      setSelectedMatchId(mid);
      setView('detail');
    }
  }, []);

  if (view === 'create') {
    return <CreateForm myTeams={myTeams} onDone={() => { setView('list'); loadList(); }} onCancel={() => setView('list')} />;
  }
  if (view === 'detail' && selectedMatchId) {
    return <DetailView matchId={selectedMatchId} myTeams={myTeams}
      onBack={() => { setView('list'); loadList(); }} />;
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 16 }}>
      <div style={{
        borderRadius: 16, padding: 20, marginBottom: 16, color: '#fff',
        background: `linear-gradient(135deg, ${BRAND_DARK}, ${BRAND})`,
      }}>
        <Title level={3} style={{ color: '#fff', margin: 0 }}>
          <RocketOutlined /> Amistosos entre times
        </Title>
        <Text style={{ color: 'rgba(255,255,255,0.9)' }}>
          Bora marcar rachão? Times abrem amistosos aqui, outros times se propõem.
        </Text>
        {myTeams.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Button type="primary" size="large" icon={<PlusOutlined />}
              onClick={() => setView('create')}
              style={{ background: '#fff', color: BRAND_DARK, border: 'none', fontWeight: 700 }}>
              Abrir amistoso do meu time
            </Button>
          </div>
        )}
      </div>

      {loading ? <Skeleton active /> :
       matches.length === 0 ? <Empty description="Nenhum amistoso aberto no momento" /> :
       <div style={{ display: 'grid', gap: 12 }}>
         {matches.map(m => (
           <Card key={m.id} hoverable onClick={() => { setSelectedMatchId(m.id); setView('detail'); }}
             style={{ borderRadius: 14 }}>
             <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
               <Avatar size={54} src={absoluteUploadUrl(m.host_logo)} icon={<RocketOutlined />}
                 style={{ background: BRAND }} />
               <div style={{ flex: 1 }}>
                 <div style={{ fontSize: 16, fontWeight: 700 }}>{m.host_name}</div>
                 <Space size={12} wrap style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                   <span><CalendarOutlined /> {fmtDate(m.date_time)}</span>
                   {m.game_type && <Tag color="blue" style={{ margin: 0 }}>{gameTypeLabel(m.game_type)}</Tag>}
                   {m.city && <span><EnvironmentOutlined /> {m.city}/{m.state}</span>}
                   {m.valor > 0 && <span><DollarOutlined /> R$ {Number(m.valor).toFixed(2)}</span>}
                 </Space>
                 {m.notes && <div style={{ marginTop: 6, fontSize: 13, color: '#555' }}>{m.notes}</div>}
               </div>
               <div style={{ textAlign: 'right' }}>
                 {m.status === 'aberto' && <Tag color="green">Aberto</Tag>}
                 {m.status === 'em_negociacao' && <Tag color="gold">Negociando</Tag>}
                 {m.proposals_count > 0 && (
                   <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                     {m.proposals_count} {m.proposals_count === 1 ? 'proposta' : 'propostas'}
                   </div>
                 )}
               </div>
             </div>
           </Card>
         ))}
       </div>
      }
    </div>
  );
}

function CreateForm({ myTeams, onDone, onCancel }) {
  const [form, setForm] = useState({ host_team_id: myTeams[0]?.id, game_type: 'society', quadra: '', date_time: null, valor: null, notes: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.host_team_id) { message.error('Escolhe um time'); return; }
    setBusy(true);
    try {
      await api.post('/community/friendlies', {
        ...form,
        date_time: form.date_time ? form.date_time.toISOString() : null,
      });
      message.success('Amistoso aberto');
      onDone();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Erro');
    } finally { setBusy(false); }
  };
  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 16 }}>
      <Button icon={<ArrowLeftOutlined />} onClick={onCancel} style={{ marginBottom: 12 }}>Voltar</Button>
      <Card title="Abrir amistoso" style={{ borderRadius: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Meu time</Text>
          <Select value={form.host_team_id} onChange={v => setForm({ ...form, host_team_id: v })}
            style={{ width: '100%', marginTop: 4 }}
            options={myTeams.map(t => ({ value: t.id, label: t.name }))} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Modalidade</Text>
          <Select value={form.game_type} onChange={v => setForm({ ...form, game_type: v })}
            style={{ width: '100%', marginTop: 4 }}
            options={GAME_TYPES.map(g => ({ value: g.value, label: g.label }))} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Quadra / local</Text>
          <Input value={form.quadra} onChange={e => setForm({ ...form, quadra: e.target.value })}
            placeholder="Ex: Arena X, quadra 2" style={{ marginTop: 4 }} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Data e hora</Text>
          <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%', marginTop: 4 }}
            value={form.date_time} onChange={v => setForm({ ...form, date_time: v })} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Valor por jogador (opcional)</Text>
          <InputNumber value={form.valor} onChange={v => setForm({ ...form, valor: v })}
            style={{ width: '100%', marginTop: 4 }} min={0} prefix="R$" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <Text strong>Observações</Text>
          <TextArea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
            rows={3} placeholder="Nível médio, sem confusão, quer time completo (10+)…" style={{ marginTop: 4 }} />
        </div>
        <Button type="primary" block size="large" onClick={submit} loading={busy}
          style={{ background: BRAND, borderColor: BRAND }}>
          Publicar amistoso
        </Button>
      </Card>
    </div>
  );
}

function DetailView({ matchId, myTeams, onBack }) {
  const [detail, setDetail] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [selectedProposalId, setSelectedProposalId] = useState(null);
  const [myTeamPicker, setMyTeamPicker] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, p] = await Promise.all([
        api.get(`/community/friendlies/${matchId}`),
        api.get(`/community/friendlies/${matchId}/proposals`).catch(() => ({ data: { proposals: [], is_host: false } })),
      ]);
      setDetail(d.data.match);
      setProposals(p.data.proposals || []);
      setIsHost(!!p.data.is_host);
    } catch { message.error('Erro'); }
  }, [matchId]);

  useEffect(() => { load(); }, [load]);

  const propose = async (teamId) => {
    try {
      await api.post(`/community/friendlies/${matchId}/proposals`, { team_id: teamId });
      message.success('Proposta enviada');
      setMyTeamPicker(false);
      load();
    } catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
  };

  const accept = async (pid) => {
    Modal.confirm({
      title: 'Fechar amistoso com esse time?', okText: 'Confirmar', cancelText: 'Cancelar',
      onOk: async () => {
        try { await api.post(`/community/friendlies/proposals/${pid}/accept`); message.success('Confirmado!'); load(); }
        catch { message.error('Erro'); }
      },
    });
  };

  const remove = () => {
    Modal.confirm({
      title: 'Apagar amistoso?',
      content: 'Vai remover propostas e conversas junto. Não dá pra desfazer.',
      okText: 'Apagar', cancelText: 'Voltar', okType: 'danger',
      onOk: async () => {
        try { await api.delete(`/community/friendlies/${matchId}`); message.success('Removido'); onBack(); }
        catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
      },
    });
  };

  const cancel = () => {
    Modal.confirm({
      title: 'Cancelar esse amistoso?',
      content: 'Fica marcado como cancelado. O time convidado consegue ver o histórico.',
      okText: 'Cancelar amistoso', cancelText: 'Voltar', okType: 'danger',
      onOk: async () => {
        try { await api.post(`/community/friendlies/${matchId}/cancel`); message.success('Cancelado'); load(); }
        catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
      },
    });
  };

  if (!detail) return <div style={{ padding: 40, textAlign: 'center' }}><Skeleton active /></div>;

  const otherTeams = (myTeams || []).filter(t => t.id !== detail.host_team_id);

  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 16 }}>
      <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: 12 }}>Voltar</Button>
      <Card style={{ borderRadius: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <Avatar size={60} src={absoluteUploadUrl(detail.host_logo)} icon={<RocketOutlined />}
            style={{ background: BRAND }} />
          <div style={{ flex: 1 }}>
            <Title level={4} style={{ margin: 0 }}>{detail.host_name}</Title>
            <Space wrap style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
              <span><CalendarOutlined /> {fmtDate(detail.date_time)}</span>
              {detail.game_type && <Tag color="blue" style={{ margin: 0 }}>{gameTypeLabel(detail.game_type)}</Tag>}
              {detail.city && <span><EnvironmentOutlined /> {detail.city}/{detail.state}</span>}
              {detail.quadra && <span>· {detail.quadra}</span>}
            </Space>
          </div>
          {detail.status === 'aberto' && <Tag color="green">Aberto</Tag>}
          {detail.status === 'em_negociacao' && <Tag color="gold">Negociando</Tag>}
          {detail.status === 'confirmado' && <Tag color="blue">Confirmado</Tag>}
          {detail.status === 'cancelado' && <Tag color="red">Cancelado</Tag>}
        </div>
        {isHost && detail.status !== 'cancelado' && (
          <Space style={{ marginTop: 12 }} wrap>
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(true)}>Editar</Button>
            {detail.status !== 'confirmado' && (
              <Button size="small" danger icon={<DeleteOutlined />} onClick={remove}>Apagar</Button>
            )}
            <Button size="small" icon={<StopOutlined />} onClick={cancel}>Cancelar amistoso</Button>
          </Space>
        )}
        {detail.notes && <div style={{ marginTop: 12, padding: 12, background: '#f5f5f5', borderRadius: 8 }}>{detail.notes}</div>}
        {detail.valor > 0 && <div style={{ marginTop: 8 }}><b>R$ {Number(detail.valor).toFixed(2)}</b> por jogador</div>}

        {!isHost && otherTeams.length > 0 && detail.status !== 'confirmado' && detail.status !== 'cancelado' && (
          <div style={{ marginTop: 16 }}>
            {!myTeamPicker && proposals.length === 0 ? (
              <Button type="primary" size="large" block onClick={() => setMyTeamPicker(true)}
                style={{ background: BRAND, borderColor: BRAND }}>
                Quero jogar esse amistoso com meu time
              </Button>
            ) : myTeamPicker ? (
              <>
                <Text strong>Qual dos meus times?</Text>
                {otherTeams.map(t => (
                  <Button key={t.id} block onClick={() => propose(t.id)} style={{ marginTop: 6, textAlign: 'left' }}>
                    {t.name}
                  </Button>
                ))}
                <Button block type="text" onClick={() => setMyTeamPicker(false)} style={{ marginTop: 6 }}>Cancelar</Button>
              </>
            ) : null}
          </div>
        )}
      </Card>

      {isHost && (
        <Card title={`Propostas recebidas (${proposals.length})`} style={{ borderRadius: 16, marginBottom: 12 }}>
          {proposals.length === 0 ? <Empty description="Nenhuma proposta ainda" /> :
            <List dataSource={proposals} renderItem={(p) => (
              <List.Item actions={[
                <Button size="small" icon={<MessageOutlined />} onClick={() => setSelectedProposalId(p.id)} key="chat">Chat</Button>,
                p.status === 'pendente' ? (
                  <Button size="small" type="primary" onClick={() => accept(p.id)} key="accept"
                    style={{ background: BRAND, borderColor: BRAND }}>Fechar amistoso</Button>
                ) : <Tag key="st">{p.status}</Tag>,
              ]}>
                <List.Item.Meta
                  avatar={<Avatar src={absoluteUploadUrl(p.team_logo)} icon={<RocketOutlined />} />}
                  title={p.team_name}
                  description={p.team_city || 'Time da comunidade'}
                />
              </List.Item>
            )} />
          }
        </Card>
      )}

      {!isHost && proposals.length > 0 && (
        <Card title="Minha proposta" style={{ borderRadius: 16, marginBottom: 12 }}>
          {proposals.map(p => (
            <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 8 }}>
              <div style={{ flex: 1 }}>
                <b>{p.team_name}</b> · <Tag>{p.status}</Tag>
              </div>
              <Button size="small" icon={<MessageOutlined />} onClick={() => setSelectedProposalId(p.id)}>
                Chat com o dono
              </Button>
            </div>
          ))}
        </Card>
      )}

      {selectedProposalId && (
        <ChatModal proposalId={selectedProposalId} onClose={() => setSelectedProposalId(null)} />
      )}
      {editing && (
        <EditModal match={detail} onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }} />
      )}
    </div>
  );
}

function EditModal({ match, onClose, onSaved }) {
  const [form, setForm] = useState({
    game_type: match.game_type || 'society',
    quadra: match.quadra || '',
    city: match.city || '',
    state: match.state || '',
    date_time: match.date_time ? dayjs(match.date_time) : null,
    valor: match.valor || null,
    notes: match.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/community/friendlies/${match.id}`, {
        ...form,
        date_time: form.date_time ? form.date_time.toISOString() : null,
      });
      message.success('Amistoso atualizado');
      onSaved();
    } catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
    setBusy(false);
  };
  return (
    <Modal open onCancel={onClose} title="Editar amistoso" onOk={save} confirmLoading={busy}
      okText="Salvar" cancelText="Cancelar" width={520}>
      <div style={{ marginBottom: 12 }}>
        <Text strong>Modalidade</Text>
        <Select value={form.game_type} onChange={v => setForm({ ...form, game_type: v })}
          style={{ width: '100%', marginTop: 4 }}
          options={GAME_TYPES.map(g => ({ value: g.value, label: g.label }))} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <Text strong>Quadra / local</Text>
        <Input value={form.quadra} onChange={e => setForm({ ...form, quadra: e.target.value })} style={{ marginTop: 4 }} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <Text strong>Data e hora</Text>
        <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: '100%', marginTop: 4 }}
          value={form.date_time} onChange={v => setForm({ ...form, date_time: v })} />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Text strong>Cidade</Text>
          <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} style={{ marginTop: 4 }} />
        </div>
        <div style={{ width: 90 }}>
          <Text strong>UF</Text>
          <Input maxLength={2} value={form.state} onChange={e => setForm({ ...form, state: e.target.value.toUpperCase() })} style={{ marginTop: 4 }} />
        </div>
      </div>
      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Text strong>Valor por jogador</Text>
        <InputNumber value={form.valor} onChange={v => setForm({ ...form, valor: v })}
          style={{ width: '100%', marginTop: 4 }} min={0} prefix="R$" />
      </div>
      <div>
        <Text strong>Observações</Text>
        <Input.TextArea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
          rows={3} style={{ marginTop: 4 }} />
      </div>
    </Modal>
  );
}

function ChatModal({ proposalId, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [me] = useState(() => {
    try { return JSON.parse(localStorage.getItem('community_user') || '{}'); } catch { return {}; }
  });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/community/friendlies/proposals/${proposalId}/messages`);
      setMsgs(r.data?.messages || []);
    } catch { /* silencioso */ }
  }, [proposalId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000); // polling barato
    return () => clearInterval(t);
  }, [load]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/community/friendlies/proposals/${proposalId}/messages`, { text: t });
      setText(''); load();
    } catch (err) { message.error(err?.response?.data?.message || 'Erro'); }
    setSending(false);
  };

  return (
    <Modal open onCancel={onClose} title="Chat da proposta" footer={null} width={520}>
      <div style={{ maxHeight: 400, overflowY: 'auto', padding: '8px 4px' }}>
        {msgs.length === 0 && <Text type="secondary">Nenhuma mensagem ainda. Manda a primeira!</Text>}
        {msgs.map(m => {
          const mine = m.from_cp_id === me.id;
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', margin: '6px 0' }}>
              <div style={{
                maxWidth: '75%', padding: '8px 12px', borderRadius: 12,
                background: mine ? BRAND : '#f0f0f0', color: mine ? '#fff' : '#333',
              }}>
                {!mine && <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 2, opacity: 0.7 }}>{m.from_name}</div>}
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{m.text}</div>
                <div style={{ fontSize: 10, opacity: 0.65, marginTop: 4, textAlign: 'right' }}>
                  {new Date(m.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Divider style={{ margin: '12px 0' }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <Input.TextArea value={text} onChange={e => setText(e.target.value)}
          placeholder="Fala aí…" rows={2} maxLength={1000}
          onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button type="primary" icon={<SendOutlined />} onClick={send} loading={sending}
          style={{ height: 'auto', background: BRAND, borderColor: BRAND }} />
      </div>
    </Modal>
  );
}
